import React, { useMemo, useRef } from 'react';

import { Icon } from '../Icon';
import { InputWrapper } from '../InputWrapper/InputWrapper';
import cx from 'classnames';
import { isEqual } from 'lodash';
import { useTranslation } from 'react-i18next';
import Select, {
  components,
  OptionProps,
  SingleValueProps,
  ValueContainerProps,
  MultiValueGenericProps,
  MultiValueRemoveProps,
  OnChangeValue,
  ActionMeta,
} from 'react-select';
import { v4 as uuid } from 'uuid';

import { FONT_AWESOME_REGULAR } from 'lane-shared/helpers/constants/icons';

import { M, XS } from '../Typography/Typography';

import ValidationMessage from '../ValidationMessage/ValidationMessage';
import {
  getStyles,
  primaryColors,
  secondaryColors,
  invisibleColors,
  darkModeColors,
} from './colors';
import AsyncSelect from 'react-select/async';

import styles from './dropdown.scss';
import { Loading } from '../Loading/Loading';
import Creatable from 'react-select/creatable';
import { Checkbox } from '../Checkbox/Checkbox';

type Color = 'primary' | 'secondary' | 'invisible';

export type DropdownItem<T> = {
  label: string;
  value: T;
  /** If true, the item is disabled and cannot be selected, but is still shown in the menu */
  isDisabled?: boolean;
  /** If true, the item is disabled and cannot be selected, and is not shown in the menu */
  isValueDisabled?: boolean;
  /** If true, the item is removed from the menu list but may remain selected */
  isHidden?: boolean;

  /** @deprecated Use isDisabled instead. Kept for backward compatibility. */
  isItemDisabled?: boolean;
};

export type BaseProps<T> = {
  onKeyPress?: (e: any) => void;
  onBlur?: (e: any) => void;
  /** optional if you want to add an identification to be returned in the onChange() */
  name?: string;
  /** dropdown theme */
  color?: Color;
  /** dropdown items */
  items?: DropdownItem<T>[];
  initialTouched?: boolean;
  // is the dropdown searchable.
  isSearchable?: boolean;
  /** placeholder if init value is not provided */
  placeholder?: string;
  className?: string | string[];
  rightIconClassName?: string;
  icon?: string;
  iconType?: string;
  rightIcon?: string;
  label?: string;
  invalid?: boolean;
  disabled?: boolean;
  /** DEPRECATED: use invalid instead, or use the ValidatedDropdown */
  errors?: string[] | null;
  success?: boolean;
  error?: boolean;
  id?: string;
  interfaceStyle?: 'dark' | 'light';
  isFullWidth?: boolean;
  menuPosition?: 'absolute' | 'fixed';
  testId?: string;
  ariaLabel?: string;
  fixedLabel?: boolean;
  closeMenuOnSelect?: boolean;
  loadOptions?: (inputValue: string) => Promise<DropdownItem<T>[]>;
  defaultOptions?: DropdownItem<T>[];
  doTranslation?: boolean;
  noOptionsMessage?: string;
  isRequired?: boolean;
  isClearable?: boolean;
  isLoading?: boolean;
  onCreateOption?: (name: string) => void;
  /**
   * Custom render function for dropdown options.
   * Receives react-select's OptionProps and should return ReactNode.
   * If provided, overrides the default IconOption rendering.
   */
  renderOption?: (props: OptionProps<DropdownItem<T>>) => React.ReactNode;
  /**
   * Custom render function for selected value display.
   * Receives react-select's SingleValueProps and should return ReactNode.
   * If provided, overrides the default CustomSelectValue rendering.
   */
  renderSelectedValue?: (props: SingleValueProps<DropdownItem<T>>) => React.ReactNode;
};

type DropdownProps<T> = BaseProps<T> & {
  isMulti?: false;
  loadOptions?: never; // if load options is not passed
  value?: T | null; // value should expect this type
  onChange?: (item: DropdownItem<T>) => void;
  onValueChange?: (value: T) => void;
  /** default initial value for dropdown */
  initialValue?: T | null;
  truncateSelectedItems?: never;
  hideSelectedOptions?: never;
};

type MultiDropdownProps<T> = BaseProps<T> & {
  isMulti?: true;
  value?: DropdownItem<T>[] | null;
  onChange?: (item: DropdownItem<T>[]) => void;
  onValueChange?: (value: T[]) => void;
  /** default initial value for dropdown */
  initialValue?: T[] | null;
  truncateSelectedItems?: boolean;
  hideSelectedOptions?: boolean;
};

type AsyncDropdownProps<T> = BaseProps<T> & {
  isMulti?: false;
  loadOptions: (inputValue: string) => Promise<DropdownItem<T>[]>; // if loadOptions is passed
  value?: null | DropdownItem<T>; // value should expect this type
  onChange?: (item: DropdownItem<T>) => void;
  onValueChange?: (value: T) => void;
  initialValue?: T | null;
  truncateSelectedItems?: never;
  hideSelectedOptions?: never;
};

export type Props<T> =
  | DropdownProps<T>
  | MultiDropdownProps<T>
  | AsyncDropdownProps<T>;

const isMultiDropdown = <T extends unknown>(
  props: Props<T>
): props is MultiDropdownProps<T> => !!props.isMulti;

BaseDropdown.defaultProps = {
  name: `Dropdown-${uuid()}`,
  color: 'primary',
  onKeyPress: () => null,
  onChange: () => null,
  onValueChange: () => null,
  onBlur: () => null,
  isSearchable: true,
  initialValue: null,
  initialTouched: false,
  placeholder: 'web.components.dropdown.selectOption',
  value: null,
  errors: null,
  success: false,
  error: false,
  id: 'dropdown',
  interfaceStyle: 'light',
  isFullWidth: false,
  menuPosition: 'fixed',
  testId: 'dropdown',
  doTranslation: true,
  isLoading: false,
};

function CustomValueContainer<T>({
  children,
  hasValue,
  getValue,
  ...props
}: ValueContainerProps<DropdownItem<T>, boolean>) {
  const { t } = useTranslation();

  if (!hasValue) {
    return (
      <components.ValueContainer {...props} hasValue={hasValue} getValue={getValue}>
        {children}
      </components.ValueContainer>
    );
  }

  const CHIPS_LIMIT = 1;
  const childrenArray = Array.isArray(children) ? children : [children];
  const [chips, otherChildren] = childrenArray;
  const chipsCount = getValue().length;

  return (
    <components.ValueContainer {...props} hasValue={hasValue} getValue={getValue}>
      {chipsCount > CHIPS_LIMIT ? (
        <span className={styles.MultiValueContainer}>
          <span className={styles.SelectedItemsLabel}>
            {t('web.components.dropdown.itemsSelected', {
              count: chipsCount,
            })}
          </span>
        </span>
      ) : (
        chips
      )}

      {otherChildren}
    </components.ValueContainer>
  );
}

export function BaseDropdown<T>(props: Props<T>) {
  const {
    className,
    items = [],
    name,
    color,
    label,
    value,
    onBlur,
    disabled,
    errors,
    success,
    invalid,
    initialValue,
    initialTouched,
    placeholder,
    isSearchable,
    icon,
    iconType,
    rightIcon,
    rightIconClassName,
    interfaceStyle,
    id,
    isFullWidth,
    menuPosition,
    testId,
    ariaLabel,
    fixedLabel = false,
    isMulti = false,
    closeMenuOnSelect = true,
    loadOptions,
    defaultOptions,
    truncateSelectedItems = false,
    doTranslation,
    noOptionsMessage,
    isRequired,
    isClearable,
    isLoading,
    onCreateOption,
    hideSelectedOptions = false,
    renderOption,
    renderSelectedValue,
  } = props;
  const [isTouched, setIsTouched] = React.useState(initialTouched);
  const [isOpened, setIsOpened] = React.useState(false);
  const { t } = useTranslation();

  let SelectComponent = loadOptions ? AsyncSelect : Select;

  if (onCreateOption) {
    SelectComponent = Creatable;
  }

  const dropdownStyles = useMemo(() => {
    switch (color) {
      case 'secondary':
        return getStyles({ colors: secondaryColors, success, invalid });
      case 'invisible':
        return getStyles({ colors: invisibleColors, success, invalid });
      default:
        return getStyles({
          colors: interfaceStyle === 'light' ? primaryColors : darkModeColors,
          success,
          invalid,
          disabled,
          icon: !!icon,
        });
    }
  }, [color, success, errors, value, disabled]);

  const allOptions = useMemo(
    () =>
      items.map(item => ({
        label: doTranslation ? t(item.label) : item.label,
        value: item.value,
        // Treat both flags as disabled in the select itself so it's rendered but not selectable
        isDisabled: item?.isDisabled || item?.isItemDisabled,
        isValueDisabled: item?.isValueDisabled,
        isHidden: item?.isHidden,
      })),
    [items]
  );

  const options = useMemo(
    () => allOptions.filter(option => !option.isHidden),
    [allOptions]
  );

  const { Option } = components;

  function IconOption(optionProps: OptionProps<DropdownItem<T>>) {
    const enrichedInnerProps = {
      ...optionProps.innerProps,
      'data-test': `${testId}-option`,
    };

    if (renderOption) {
      return (
        <Option {...optionProps} innerProps={enrichedInnerProps}>
          {renderOption(optionProps)}
        </Option>
      );
    }

    return (
      <Option {...optionProps} innerProps={enrichedInnerProps}>
        {/* @ts-expect-error ts-migrate(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message */}
        {icon ? <Icon set="FontAwesome" name={icon} type={iconType} /> : null}
        {optionProps?.data?.label}
      </Option>
    );
  }

  function CustomSelectValue(singleValueProps: SingleValueProps<DropdownItem<T>>) {
    // Use custom renderSelectedValue if provided
    if (renderSelectedValue) {
      return (
        <div
          className={
            isOpened ? styles.hideCustomSelectValue : styles.customSelectValue
          }
        >
          {renderSelectedValue(singleValueProps)}
        </div>
      );
    }
    
    // Default behavior
    return (
      <div
        className={
          isOpened ? styles.hideCustomSelectValue : styles.customSelectValue
        }
        data-interface-style={interfaceStyle}
        data-option-with-icon={Boolean(icon)}
      >
        {/* @ts-expect-error ts-migrate(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message */}
        {icon ? <Icon set="FontAwesome" name={icon} type={iconType} /> : null}
        <M>{singleValueProps?.data?.label}</M>
      </div>
    );
  }

  function CustomPlaceHolder() {
    const isLabelShown = label && !isTouched && !fixedLabel;
    const placeHolderText =
      doTranslation || placeholder === BaseDropdown.defaultProps.placeholder
        ? t(placeholder || '')
        : placeholder;
    const placeholderToUse = isLabelShown ? label : placeHolderText;

    return (
      <div
        className={
          isOpened ? styles.hideCustomSelectValue : styles.customSelectValue
        }
        data-option-with-icon={Boolean(icon)}
      >
        {/* @ts-expect-error ts-migrate(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message */}
        <Icon set="FontAwesome" name={icon} type={iconType} />
        <span className="placeholderText">{placeholderToUse}</span>
      </div>
    );
  }

  function CustomIndicatorError() {
    return (
      <div className={styles.CustomIndicatorError}>
        {/* @ts-expect-error ts-migrate(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message */}
        <Icon set="FontAwesome" name="exclamation-circle" type={iconType} />
      </div>
    );
  }

  function CustomIndicator() {
    return (
      <div className={cx(styles.CustomIndicator, rightIconClassName)}>
        <Icon
          set="FontAwesome"
          name={rightIcon || 'angle-down'}
          // @ts-ignore Type 'string | undefined' is not assignable to typ...
          type={iconType || FONT_AWESOME_REGULAR}
          testId="angle-down"
        />
      </div>
    );
  }

  function CustomMultiValueContainer({
    children,
    innerProps,
    data,
  }: MultiValueGenericProps<DropdownItem<T>, boolean>) {
    return (
      <span
        {...innerProps}
        className={cx(
          styles.MultiValueContainer,
          data?.isValueDisabled && styles.DisabledItem
        )}
      >
        {children}
      </span>
    );
  }

  function CustomMultiOption(props: OptionProps<DropdownItem<T>, boolean>) {
    const optionIsDisabled = Boolean(
      // Prefer props.isDisabled from react-select; fallback to data.isDisabled
      (props as any).isDisabled || props.data?.isDisabled
    );

    const optionInlineStyle = props.getStyles(
      'option',
      props
    ) as unknown as React.CSSProperties;

    return (
      <M
        className={cx(optionIsDisabled && styles.DisabledItem)}
        ref={props.innerRef}
        {...props.innerProps}
        data-test={`${testId}-option`}
        style={optionInlineStyle}
      >
        <Checkbox
          style={dropdownStyles.optionCheckboxContainer()}
          selected={props.isSelected}
          value={props.getValue()}
          disabled={optionIsDisabled}
          onChange={() => {
            if (!optionIsDisabled) {
              props.selectOption(props.data);
            }
          }}
        />
        <p className="text-nowrap overflow-hidden text-ellipsis">
          {props.children}
        </p>
      </M>
    );
  }

  function CustomMultiValueRemove(props: MultiValueRemoveProps<DropdownItem<T>, boolean>) {
    return (
      <div
        data-test="customMultiValueRemoveIcon"
        style={{ backgroundColor: 'none' }}
        {...props.innerProps}
        className=""
      >
        {!(props.data?.isDisabled || props.data?.isValueDisabled) && (
          <Icon name="times" />
        )}
      </div>
    );
  }

  const getValue = (): DropdownItem<T> | DropdownItem<T>[] | null | undefined => {
    if (loadOptions) {
      return value as DropdownItem<T> | null | undefined;
    }

    if (isMulti) {
      if (allOptions.length === 0) {
        return (value as DropdownItem<T>[] | null | undefined) ?? null;
      }

      return Array.isArray(value)
        ? allOptions.filter(option =>
            value.some(
              selectValue =>
                (selectValue as DropdownItem<T>).value === option.value
            )
          )
        : [];
    }

    return value ? allOptions.find(option => isEqual(option.value, value)) ?? null : null;
  };

  const containerRef = useRef(null);
  // todo: remove ValidationMessage and errors prop once code base is refactored

  return (
    <InputWrapper
      label={fixedLabel ? label : undefined}
      isRequired={isRequired}
      id={id}
    >
      <div
        className={styles.mainContainer}
        ref={containerRef}
        data-test={testId}
      >
        <SelectComponent
          isLoading={isLoading}
          minMenuHeight={Math.min(options.length, 5) * 41}
          loadingMessage={() => <Loading />}
          inputId={id}
          isDisabled={disabled}
          openMenuOnFocus={!isMulti}
          className={cx(
            styles.Dropdown,
            isFullWidth && styles.fullWidth,
            className
          )}
          placeholder={placeholder}
          defaultValue={
            initialValue &&
            allOptions.find(option => isEqual(option.value, initialValue))
          }
          value={getValue()}
          onChange={(
            newValue: OnChangeValue<DropdownItem<T>, boolean>,
            _actionMeta: ActionMeta<DropdownItem<T>>
          ) => {
            if (isMultiDropdown(props)) {
              const selectedOptions = Array.isArray(newValue)
                ? newValue.filter(Boolean)
                : [];
              if (props.onChange) {
                props.onChange(selectedOptions);
              }
              if (props.onValueChange) {
                props.onValueChange(selectedOptions.map(option => option.value));
              }
            } else {
              const selectedOption = Array.isArray(newValue)
                ? newValue[0]
                : newValue;
              if (selectedOption) {
                if (props.onChange) {
                  props.onChange(selectedOption);
                }
                if (props.onValueChange) {
                  props.onValueChange(selectedOption.value);
                }
              }
            }
          }}
          tabSelectsValue={!isMulti}
          onMenuOpen={() => {
            setIsOpened(true);
          }}
          onMenuClose={() => {
            setIsOpened(false);
          }}
          onFocus={() => {
            if (!isTouched) setIsTouched(true);
          }}
          onBlur={onBlur}
          isSearchable={isSearchable}
          name={name}
          options={options}
          components={{
            SingleValue: CustomSelectValue,
            Option: IconOption,
            Placeholder: CustomPlaceHolder,
            IndicatorSeparator: invalid ? CustomIndicatorError : null,
            DropdownIndicator: CustomIndicator,
            ...(isMulti
              ? {
                  Option: CustomMultiOption,
                  ValueContainer: truncateSelectedItems
                    ? CustomValueContainer
                    : components.ValueContainer,
                  MultiValueContainer: CustomMultiValueContainer,
                  MultiValueRemove: CustomMultiValueRemove,
                }
              : {}),
          }}
          styles={dropdownStyles}
          closeMenuOnScroll={(e: any) => {
            if (!containerRef.current) return false;
            return e.target.contains(containerRef.current);
          }}
          blurInputOnSelect={false}
          menuPosition={menuPosition}
          menuShouldScrollIntoView={false}
          menuShouldBlockScroll={true}
          menuPlacement="auto"
          aria-label={ariaLabel}
          noOptionsMessage={() => noOptionsMessage ?? t('No options')}
          hideSelectedOptions={hideSelectedOptions}
          closeMenuOnSelect={closeMenuOnSelect}
          isMulti={isMulti}
          defaultOptions={defaultOptions}
          loadOptions={loadOptions}
          isClearable={isClearable}
          {...(onCreateOption ? ({ onCreateOption } as { onCreateOption: (name: string) => void }) : {})}
        />
        {label && !fixedLabel && (
          <label
            htmlFor={id}
            className={styles.label}
            data-touched={isTouched}
            data-interface-style={interfaceStyle}
          >
            <XS>{label}</XS>
          </label>
        )}

        {invalid && (
          <ValidationMessage
            errors={errors}
            iconType={iconType}
            withoutIcon
            doTranslation={doTranslation}
          />
        )}
      </div>
    </InputWrapper>
  );
}

export const Dropdown = <T,>(
  props: DropdownProps<T> | AsyncDropdownProps<T>
) => <BaseDropdown {...props} />;
